// 共同作業機能関連の型定義

import type { Examination } from './evaluation';

export type CollaborationRole = 'owner' | 'collaborator' | 'viewer';
export type CollaborationStatus = 'pending' | 'accepted' | 'declined' | 'removed';

// 評価プロジェクトの権限定義
export interface ExaminationPermissions {
  canView: boolean;
  canEdit: boolean;
  canAddProducts: boolean;
  canAddCriteria: boolean;
  canEvaluate: boolean;
  canSetGrades: boolean;
  canInviteUsers: boolean;
  canRemoveUsers: boolean;
  canDeleteExamination: boolean;
}

// 共同作業者情報
export interface CollaboratorInfo {
  userId: string;
  email: string;
  fullName: string;
  role: CollaborationRole;
  status: CollaborationStatus;
  invitedAt: string;
  acceptedAt?: string;
}

// 招待リクエスト
export interface InvitationRequest {
  email: string;
  role: CollaborationRole;
}

// 招待受諾リクエスト
export interface AcceptInvitationRequest {
  token: string;
}

// 活動ログエントリ
export interface ActivityLogEntry {
  id: number;
  userId: string;
  userFullName: string;
  action: string;
  targetType?: string;
  targetId?: string;
  createdAt: string;
}

// 共有設定
export interface SharingSettings {
  allowInvites: boolean;
  defaultRole: CollaborationRole;
}

// 拡張された試験レスポンス（共同作業情報付き）
export interface ExaminationWithCollaboration extends Examination {
  isShared: boolean;
  sharingSettings: SharingSettings;
  currentUserRole?: CollaborationRole;
  collaborators?: CollaboratorInfo[];
}

// 役割に基づく権限マッピング
export const rolePermissions: Record<CollaborationRole, ExaminationPermissions> = {
  owner: {
    canView: true,
    canEdit: true,
    canAddProducts: true,
    canAddCriteria: true,
    canEvaluate: true,
    canSetGrades: true,
    canInviteUsers: true,
    canRemoveUsers: true,
    canDeleteExamination: true,
  },
  collaborator: {
    canView: true,
    canEdit: true,
    canAddProducts: true,
    canAddCriteria: true,
    canEvaluate: true,
    canSetGrades: true,
    canInviteUsers: false,
    canRemoveUsers: false,
    canDeleteExamination: false,
  },
  viewer: {
    canView: true,
    canEdit: false,
    canAddProducts: false,
    canAddCriteria: false,
    canEvaluate: false,
    canSetGrades: false,
    canInviteUsers: false,
    canRemoveUsers: false,
    canDeleteExamination: false,
  },
};

// 権限チェック関数
export function hasPermission(
  role: CollaborationRole, 
  permission: keyof ExaminationPermissions
): boolean {
  return rolePermissions[role][permission];
}

// 活動アクションのローカライズ
export const activityActionLabels: Record<string, string> = {
  invite_collaborator: '共同作業者を招待',
  accept_invitation: '招待を受諾',
  remove_collaborator: '共同作業者を削除',
  update_examination: '評価プロジェクトを更新',
  add_product: '評価対象を追加',
  update_product: '評価対象を更新',
  delete_product: '評価対象を削除',
  add_criterion: '評価観点を追加',
  update_criterion: '評価観点を更新',
  delete_criterion: '評価観点を削除',
  update_evaluation: '評価を更新',
  set_grades: '等級を設定',
};
